import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, Play, Square, RefreshCw, TrendingUp, TrendingDown,
  Clock, Shield, Activity, CheckCircle, Settings, Eye, 
  Loader2, Wifi, WifiOff, Wallet, Bitcoin, Sliders,
  DollarSign, BarChart3, Target, AlertCircle, Bell, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_VPS_URL = 'http://91.197.235.201';

// Crypto icons mapping
const CRYPTO_COLORS = {
  BTC: 'from-orange-500 to-amber-500',
  ETH: 'from-blue-500 to-indigo-500',
  SOL: 'from-purple-500 to-pink-500',
  XRP: 'from-gray-400 to-gray-500',
  DOGE: 'from-yellow-400 to-amber-400',
  HYPE: 'from-cyan-400 to-blue-400',
  BNB: 'from-yellow-500 to-orange-500',
};

// Main Bot Panel Component
const BotPanel = ({ apiKey, baseUrl }) => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [markets, setMarkets] = useState({});
  const [starting, setStarting] = useState(false);
  const [autoExecute, setAutoExecute] = useState(false);
  
  const vpsUrl = `${baseUrl}:8083`;

  const proxyFetch = async (endpoint, options = {}) => {
    const headers = { 
      'Content-Type': 'application/json', 
      'X-VPS-URL': vpsUrl,
      ...(apiKey ? { 'X-VPS-Key': apiKey } : {}), 
      ...options.headers 
    };
    return fetch(`${API_URL}/api/vps/selenium5min${endpoint}`, { ...options, headers });
  };

  const testConnection = async () => {
    setConnecting(true);
    try {
      const response = await proxyFetch('/status');
      if (response.ok) {
        const result = await response.json();
        if (result.running !== undefined || result.data) { 
          setConnected(true); 
          setStatus(result.data || result); 
          if (result.config?.auto_execute !== undefined) {
            setAutoExecute(result.config.auto_execute);
          }
          toast.success('Conectado al bot');
        }
      } else { 
        setConnected(false); 
        toast.error('Bot no disponible');
      }
    } catch (error) { 
      setConnected(false); 
      toast.error('Error de conexión');
    }
    finally { setConnecting(false); }
  };

  const fetchStatus = useCallback(async () => {
    if (!connected) return;
    try {
      const [statusRes, pnlRes, marketsRes] = await Promise.all([
        proxyFetch('/status'),
        proxyFetch('/pnl'),
        proxyFetch('/markets').catch(() => null)
      ]);
      
      if (statusRes.ok) { 
        const result = await statusRes.json(); 
        setStatus(result.data || result);
        if (result.config?.auto_execute !== undefined) {
          setAutoExecute(result.config.auto_execute);
        }
        if (result.markets) {
          setMarkets(result.markets);
        }
      }
      
      if (pnlRes?.ok) {
        const pnlResult = await pnlRes.json();
        setPnlData(pnlResult.data || pnlResult);
      }
    } catch (error) { console.error('Status error:', error); }
  }, [connected, vpsUrl, apiKey]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const response = await proxyFetch('/start', { 
        method: 'POST', 
        body: JSON.stringify({ auto_execute: autoExecute }) 
      });
      const result = await response.json();
      if (result.success || result.data?.success) { 
        toast.success('Bot iniciado');
        fetchStatus(); 
      } else { 
        toast.error(result.error || 'Error al iniciar'); 
      }
    } catch (error) { toast.error('Error de conexión'); }
    finally { setStarting(false); }
  };

  const handleStop = async () => {
    try {
      const response = await proxyFetch('/stop', { method: 'POST' });
      const result = await response.json();
      if (result.success || result.data?.success) { 
        toast.success('Bot detenido'); 
        fetchStatus(); 
      }
    } catch (error) { toast.error('Error de conexión'); }
  };

  useEffect(() => { 
    if (baseUrl) testConnection(); 
  }, [baseUrl]);
  
  useEffect(() => { 
    if (connected) { 
      fetchStatus(); 
      const interval = setInterval(fetchStatus, 10000); 
      return () => clearInterval(interval); 
    } 
  }, [connected, fetchStatus]);

  const formatUptime = (seconds) => { 
    if (!seconds) return '0s'; 
    const h = Math.floor(seconds / 3600); 
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`; 
    if (m > 0) return `${m}m`; 
    return `${Math.floor(seconds)}s`; 
  };

  return (
    <div className="space-y-6">
      {/* Connection Card */}
      <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="bot-panel">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg shadow-amber-500/20">
                <Bitcoin className="w-7 h-7 text-black" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2 text-xl text-amber-50">
                  Invictus Multi-Crypto Bot
                  <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">
                    Puerto 8083
                  </Badge>
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  7 mercados crypto • Sizing dinámico • CLOB execution
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {connected ? (
                <Badge className="gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 border-amber-500/30">
                  <Wifi className="w-3 h-3" />Online
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1 px-3 py-1">
                  <WifiOff className="w-3 h-3" />Offline
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {!connected ? (
            <Button 
              onClick={testConnection} 
              disabled={connecting} 
              className="w-full h-12 text-base bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-400 hover:to-amber-500"
              data-testid="connect-btn"
            >
              {connecting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Wifi className="w-5 h-5 mr-2" />}
              {connecting ? 'Conectando...' : 'Conectar al Bot'}
            </Button>
          ) : (
            <>
              {/* Status Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl text-center bg-amber-500/5 border border-amber-500/20">
                  <div className="text-xs text-muted-foreground mb-1">Estado</div>
                  <div className={cn("font-bold text-lg", status?.running ? "text-emerald-400" : "text-gray-400")}>
                    {status?.running ? 'Activo' : 'Parado'}
                  </div>
                </div>
                <div className="p-3 rounded-xl text-center bg-amber-500/5 border border-amber-500/20">
                  <div className="text-xs text-muted-foreground mb-1">Scans</div>
                  <div className="font-bold text-lg text-amber-400">{status?.scans || 0}</div>
                </div>
                <div className="p-3 rounded-xl text-center bg-amber-500/5 border border-amber-500/20">
                  <div className="text-xs text-muted-foreground mb-1">Trades</div>
                  <div className="font-bold text-lg text-amber-400">{status?.trades || 0}</div>
                </div>
                <div className="p-3 rounded-xl text-center bg-amber-500/5 border border-amber-500/20">
                  <div className="text-xs text-muted-foreground mb-1">Uptime</div>
                  <div className="font-bold text-lg text-amber-400">{formatUptime(status?.uptime_seconds)}</div>
                </div>
              </div>

              {/* Cryptos Enabled */}
              <div className="flex flex-wrap gap-2">
                {(status?.config?.enabled_cryptos || ['BTC','ETH','SOL','XRP','DOGE','HYPE','BNB']).map((crypto) => (
                  <Badge 
                    key={crypto} 
                    className={cn("px-3 py-1 text-white border-0 bg-gradient-to-r", CRYPTO_COLORS[crypto] || 'from-gray-500 to-gray-600')}
                  >
                    {crypto}
                  </Badge>
                ))}
              </div>

              {/* CLOB Status */}
              <div className={cn("flex items-center justify-between p-3 rounded-xl border", 
                status?.clob_ready ? "bg-emerald-500/10 border-emerald-500/30" : "bg-yellow-500/10 border-yellow-500/30"
              )}>
                <div className="flex items-center gap-2">
                  <Activity className={status?.clob_ready ? "w-4 h-4 text-emerald-400" : "w-4 h-4 text-yellow-400"} />
                  <span className="text-sm font-medium">CLOB Trading</span>
                </div>
                <Badge variant={status?.clob_ready ? "default" : "secondary"} className={status?.clob_ready ? "bg-emerald-500/20 text-emerald-400" : ""}>
                  {status?.clob_ready ? 'Activo' : 'No configurado'}
                </Badge>
              </div>

              {/* Auto-Execute Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <span className="font-medium">Auto-Execute Trades</span>
                </div>
                <Switch 
                  checked={autoExecute} 
                  onCheckedChange={setAutoExecute}
                  data-testid="auto-execute-switch"
                />
              </div>

              {/* Controls */}
              <div className="flex gap-3">
                {!status?.running ? (
                  <Button 
                    onClick={handleStart} 
                    disabled={starting} 
                    className="flex-1 h-11 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-400 hover:to-amber-500"
                    data-testid="start-btn"
                  >
                    {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2" />}
                    Iniciar Bot
                  </Button>
                ) : (
                  <Button onClick={handleStop} variant="destructive" className="flex-1 h-11" data-testid="stop-btn">
                    <Square className="w-5 h-5 mr-2" />Detener Bot
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={fetchStatus} 
                  className="h-11 px-4 border-amber-500/30 hover:bg-amber-500/10"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const res = await proxyFetch('/report/send', { method: 'POST' });
                      if (res.ok) toast.success('Reporte enviado a Telegram');
                      else toast.error('Error enviando reporte');
                    } catch { toast.error('Error de conexión'); }
                  }}
                  className="h-11 px-4 border-amber-500/30 hover:bg-amber-500/10"
                  data-testid="report-btn"
                  title="Enviar reporte a Telegram"
                >
                  <Bell className="w-5 h-5" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* PnL Card */}
      {connected && pnlData && (
        <Card className="border-amber-500/30" data-testid="pnl-panel">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg text-amber-50">Performance & PnL</CardTitle>
                <CardDescription>Métricas de trading en tiempo real</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-center">
                <div className="text-xs text-muted-foreground mb-1">Total Trades</div>
                <div className="text-2xl font-bold text-amber-400">{pnlData.total || 0}</div>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                <div className="text-xs text-muted-foreground mb-1">Posiciones</div>
                <div className="text-2xl font-bold text-emerald-400">{pnlData.positions || 0}</div>
              </div>
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-center">
                <div className="text-xs text-muted-foreground mb-1">Valor Total</div>
                <div className="text-2xl font-bold text-blue-400">${pnlData.total_value?.toFixed(2) || '0.00'}</div>
              </div>
              <div className={cn("p-4 rounded-xl text-center border",
                (pnlData.total_pnl || 0) >= 0 
                  ? "bg-emerald-500/10 border-emerald-500/30" 
                  : "bg-red-500/10 border-red-500/30"
              )}>
                <div className="text-xs text-muted-foreground mb-1">PnL Total</div>
                <div className={cn("text-2xl font-bold flex items-center justify-center gap-1",
                  (pnlData.total_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                )}>
                  {(pnlData.total_pnl || 0) >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  ${Math.abs(pnlData.total_pnl || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* PnL by Crypto */}
            {pnlData.by_crypto && Object.keys(pnlData.by_crypto).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Trades por Crypto</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(pnlData.by_crypto).map(([crypto, data]) => (
                    <div key={crypto} className="p-3 rounded-lg bg-muted/30 border border-muted">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={cn("text-xs text-white border-0 bg-gradient-to-r", CRYPTO_COLORS[crypto] || 'from-gray-500 to-gray-600')}>
                          {crypto}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{data.trades} trades</span>
                      </div>
                      <div className="text-sm font-mono text-amber-400">${data.amount?.toFixed(2) || '0.00'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Configuration Panel
const ConfigPanel = ({ apiKey, baseUrl }) => {
  const [config, setConfig] = useState({
    min_confidence: 60,
    min_position_size: 2,
    max_position_size: 25,
    scan_interval: 5,
    auto_execute: false,
    telegram_enabled: true,
    min_price_diff: 0.03,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const vpsUrl = `${baseUrl}:8083`;

  const proxyFetch = async (endpoint, options = {}) => {
    const headers = { 
      'Content-Type': 'application/json', 
      'X-VPS-URL': vpsUrl,
      ...(apiKey ? { 'X-VPS-Key': apiKey } : {}), 
      ...options.headers 
    };
    return fetch(`${API_URL}/api/vps/selenium5min${endpoint}`, { ...options, headers });
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await proxyFetch('/config');
        if (response.ok) {
          const result = await response.json();
          const configData = result.data?.config || result.config || result.data || result;
          setConfig(prev => ({ ...prev, ...configData }));
        }
      } catch (error) { console.error('Config load error:', error); }
      finally { setLoading(false); }
    };
    fetchConfig();
  }, [vpsUrl]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await proxyFetch('/config', {
        method: 'POST',
        body: JSON.stringify(config),
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Configuración guardada');
      } else {
        toast.error(result.error || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-500/30" data-testid="config-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600">
            <Sliders className="w-6 h-6 text-black" />
          </div>
          <div>
            <CardTitle className="text-lg text-amber-50">Configuración del Bot</CardTitle>
            <CardDescription>Ajusta los parámetros de trading</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Confianza Mínima</Label>
                  <span className="font-bold text-amber-400">{config.min_confidence}%</span>
                </div>
                <Slider
                  value={[config.min_confidence]}
                  onValueChange={([v]) => setConfig(prev => ({ ...prev, min_confidence: v }))}
                  min={30} max={95} step={5}
                  className="[&_[role=slider]]:bg-amber-500"
                />
                <p className="text-xs text-muted-foreground mt-1">Solo operar cuando la confianza supere este %</p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Position Size Mínimo</Label>
                  <span className="font-bold text-amber-400">${config.min_position_size}</span>
                </div>
                <Slider
                  value={[config.min_position_size]}
                  onValueChange={([v]) => setConfig(prev => ({ ...prev, min_position_size: v }))}
                  min={1} max={20} step={1}
                  className="[&_[role=slider]]:bg-amber-500"
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Position Size Máximo</Label>
                  <span className="font-bold text-amber-400">${config.max_position_size}</span>
                </div>
                <Slider
                  value={[config.max_position_size]}
                  onValueChange={([v]) => setConfig(prev => ({ ...prev, max_position_size: v }))}
                  min={5} max={100} step={5}
                  className="[&_[role=slider]]:bg-amber-500"
                />
                <p className="text-xs text-muted-foreground mt-1">Máximo cuando confianza es alta</p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Min Price Diff (Edge)</Label>
                  <span className="font-bold text-amber-400">{(config.min_price_diff * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[config.min_price_diff * 100]}
                  onValueChange={([v]) => setConfig(prev => ({ ...prev, min_price_diff: v / 100 }))}
                  min={1} max={15} step={1}
                  className="[&_[role=slider]]:bg-amber-500"
                />
                <p className="text-xs text-muted-foreground mt-1">Diferencia mínima UP/DOWN para operar</p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Intervalo de Scan</Label>
                  <span className="font-bold text-amber-400">{config.scan_interval}s</span>
                </div>
                <Slider
                  value={[config.scan_interval]}
                  onValueChange={([v]) => setConfig(prev => ({ ...prev, scan_interval: v }))}
                  min={3} max={60} step={1}
                  className="[&_[role=slider]]:bg-amber-500"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <span className="font-medium">Auto-Execute</span>
                </div>
                <Switch 
                  checked={config.auto_execute} 
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, auto_execute: v }))} 
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-amber-400" />
                  <span className="font-medium">Alertas Telegram</span>
                </div>
                <Switch 
                  checked={config.telegram_enabled} 
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, telegram_enabled: v }))} 
                />
              </div>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={saving} 
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-400 hover:to-amber-500"
              data-testid="save-config-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
              Guardar Configuración
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Trades History Panel
const TradesPanel = ({ apiKey, baseUrl }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);

  const vpsUrl = `${baseUrl}:8083`;

  const proxyFetch = async (endpoint, options = {}) => {
    const headers = { 
      'Content-Type': 'application/json', 
      'X-VPS-URL': vpsUrl,
      ...(apiKey ? { 'X-VPS-Key': apiKey } : {}), 
      ...options.headers 
    };
    return fetch(`${API_URL}/api/vps/selenium5min${endpoint}`, { ...options, headers });
  };

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const response = await proxyFetch('/trades-history');
      if (response.ok) {
        const result = await response.json();
        setTrades(result.data || []);
      }
    } catch (error) {
      toast.error('Error cargando historial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-amber-500/30" data-testid="trades-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-amber-50">Historial de Trades</CardTitle>
              <CardDescription>Operaciones ejecutadas por el bot</CardDescription>
            </div>
          </div>
          <Button variant="outline" onClick={fetchTrades} disabled={loading} className="border-amber-500/30 hover:bg-amber-500/10">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {trades.length === 0 ? (
          <div className="text-center py-12">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">Carga el historial de trades</p>
            <Button onClick={fetchTrades} disabled={loading} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Cargar Historial
            </Button>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
            {trades.map((trade, idx) => (
              <div key={idx} className="p-3 rounded-lg bg-muted/30 border flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Badge className={cn("text-white border-0 bg-gradient-to-r", CRYPTO_COLORS[trade.crypto] || 'from-gray-500 to-gray-600')}>
                    {trade.crypto}
                  </Badge>
                  <Badge variant={trade.direction === 'UP' ? 'default' : 'secondary'} className={trade.direction === 'UP' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                    {trade.direction}
                  </Badge>
                  <span className="text-sm font-mono">${trade.amount?.toFixed(2)}</span>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">
                    {new Date(trade.ts).toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-400">
                    {trade.conf}% conf
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
export const VPSControl = () => {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('vps_api_key') || '');
  const [showApiKey, setShowApiKey] = useState(false);
  const [baseUrl, setBaseUrl] = useState(() => localStorage.getItem('vps_base_url') || DEFAULT_VPS_URL);
  const [activeTab, setActiveTab] = useState('bot');

  useEffect(() => {
    localStorage.setItem('vps_api_key', apiKey);
    localStorage.setItem('vps_base_url', baseUrl);
  }, [apiKey, baseUrl]);

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="vps-control">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold flex items-center justify-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Zap className="w-8 h-8 text-black" />
          </div>
          <span className="bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">
            Invictus HFT
          </span>
        </h1>
        <p className="text-muted-foreground mt-2">Bot Multi-Crypto para Polymarket • Dublin VPS</p>
      </div>

      {/* Connection Settings */}
      <Card className="border-amber-500/20">
        <CardContent className="pt-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>IP del VPS Dublin</Label>
              <Input 
                value={baseUrl} 
                onChange={(e) => setBaseUrl(e.target.value)} 
                placeholder="http://91.197.235.201" 
                className="font-mono bg-background"
                data-testid="vps-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>API Key (opcional)</Label>
              <div className="flex gap-2">
                <Input 
                  type={showApiKey ? "text" : "password"} 
                  value={apiKey} 
                  onChange={(e) => setApiKey(e.target.value)} 
                  placeholder="Tu API Key" 
                  className="font-mono bg-background"
                  data-testid="api-key-input"
                />
                <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)} className="border-amber-500/30">
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-12 bg-muted/30">
          <TabsTrigger value="bot" className="gap-2 text-base data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-bot">
            <Zap className="w-4 h-4" />Bot
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2 text-base data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-history">
            <Wallet className="w-4 h-4" />Historial
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2 text-base data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-config">
            <Sliders className="w-4 h-4" />Config
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bot" className="mt-5">
          <BotPanel apiKey={apiKey} baseUrl={baseUrl} />
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <TradesPanel apiKey={apiKey} baseUrl={baseUrl} />
        </TabsContent>

        <TabsContent value="config" className="mt-5">
          <ConfigPanel apiKey={apiKey} baseUrl={baseUrl} />
        </TabsContent>
      </Tabs>

      {/* System Info */}
      <Card className="bg-gradient-to-br from-amber-500/5 to-transparent border-amber-500/20">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="w-10 h-10 text-amber-400" />
              <div>
                <h3 className="font-semibold text-lg text-amber-50">Sistema Invictus</h3>
                <p className="text-sm text-muted-foreground">VPS Dublin, Ireland • Puerto 8083 • 7 Mercados Crypto</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-amber-400">24/7</div>
              <div className="text-sm text-muted-foreground">Trading Activo</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VPSControl;
